import {
  useGetIdentity,
  useGetList,
  useGetOne,
  useRecordContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { useIsMobile } from "@/hooks/use-mobile";

import { isInLeadManagerScope } from "../providers/commons/canAccess";
import type {
  AppRole,
  BusinessArm,
  Contact,
  LiderTeamMember,
  Sale,
} from "../types";

const MAX_OWNER_CHOICES = 200;

// Roles that can own a contact (mirrors `private.is_commercial()`).
const OWNER_ROLES: readonly AppRole[] = [
  "direccion",
  "lead_manager",
  "lider",
  "asesora",
];

const fullName = (person: { first_name: string; last_name: string }) =>
  `${person.first_name} ${person.last_name}`;

/**
 * Almikia Epic EC2a: "Responsable" (`sales_id`) of a contact. Hidden for the asesora (she is
 * the owner: the DB trigger defaults it). direccion and lead_manager choose any owner (a
 * lead_manager within her brazo); a lider chooses on CREATE only, among herself and her team
 * (`lider_team_members`), and sees it read-only when editing. This is cosmetic: RLS and the
 * guard trigger are the authority.
 */
export const ContactResponsableInput = () => {
  const { identity } = useGetIdentity();
  const record = useRecordContext<Contact>();
  const role = identity?.role as AppRole | undefined;
  const isCreate = record?.id == null;

  if (role === "direccion" || role === "lead_manager") {
    return <ManagerOwnerInput role={role} />;
  }
  if (role === "lider") {
    return isCreate ? <LiderOwnerInput /> : <ReadOnlyOwner />;
  }
  return null;
};

const ManagerOwnerInput = ({ role }: { role: AppRole }) => {
  const { identity } = useGetIdentity();
  const record = useRecordContext<Contact>();
  const isMobile = useIsMobile();
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: MAX_OWNER_CHOICES },
    sort: { field: "last_name", order: "ASC" },
  });
  const actor = {
    id: identity?.id,
    business_arm: identity?.business_arm as BusinessArm | null | undefined,
  };
  const isCurrentOwner = (sale: Sale) => sale.id === record?.sales_id;
  const choices = (sales ?? [])
    .filter(
      (sale) =>
        isCurrentOwner(sale) ||
        (OWNER_ROLES.includes(sale.role) &&
          !sale.disabled &&
          (role === "lead_manager" ? isInLeadManagerScope(actor, sale) : true)),
    )
    .map((sale) => ({ id: sale.id, name: fullName(sale) }));
  return <OwnerAutocomplete choices={choices} modal={isMobile} />;
};

const LiderOwnerInput = () => {
  const { identity } = useGetIdentity();
  const isMobile = useIsMobile();
  const { data: team } = useGetList<LiderTeamMember>("lider_team_members", {
    pagination: { page: 1, perPage: MAX_OWNER_CHOICES },
  });
  const own =
    identity?.id != null
      ? [{ id: identity.id, name: identity.fullName ?? "" }]
      : [];
  const members = (team ?? [])
    .filter((member) => member.id !== identity?.id)
    .map((member) => ({ id: member.id, name: fullName(member) }));
  return <OwnerAutocomplete choices={[...own, ...members]} modal={isMobile} />;
};

const OwnerAutocomplete = ({
  choices,
  modal,
}: {
  choices: { id: Identifier; name: string }[];
  modal: boolean;
}) => (
  <AutocompleteInput
    source="sales_id"
    label="resources.contacts.fields.sales_id"
    choices={choices}
    translateChoice={false}
    helperText={false}
    modal={modal}
  />
);

const ReadOnlyOwner = () => {
  const translate = useTranslate();
  const record = useRecordContext<Contact>();
  const { data: owner } = useGetOne<Sale>(
    "sales",
    { id: record?.sales_id },
    { enabled: record?.sales_id != null },
  );
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">
        {translate("resources.contacts.fields.sales_id")}
      </span>
      <span className="text-base md:text-sm">
        {owner ? fullName(owner) : "-"}
      </span>
    </div>
  );
};
